import { useState, useEffect, useCallback } from "react";
import AdaptiveLearningApi from "shared/services/AdaptiveLearningApi";

/**
 * Hook to fetch student progress for all concepts
 * @param {number} studentId - Student ID
 * @param {boolean} autoFetch - Auto fetch on mount (default: true)
 * @returns {Object} { progress, loading, error, refetch }
 */
export const useAdaptiveLearningProgress = (studentId, autoFetch = true) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    if (!studentId) {
      setError("Student ID is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await AdaptiveLearningApi.getProgress(studentId);
      setProgress(data);
    } catch (err) {
      console.error("Error fetching progress:", err);
      setError(err.message || "Failed to fetch progress");
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (autoFetch && studentId) {
      fetchProgress();
    }
  }, [autoFetch, studentId, fetchProgress]);

  return {
    progress,
    loading,
    error,
    refetch: fetchProgress,
  };
};

/**
 * Hook to fetch progress for a specific concept
 * @param {number} studentId - Student ID
 * @param {string} conceptCode - Concept code
 * @param {boolean} autoFetch - Auto fetch on mount (default: true)
 * @returns {Object} { conceptProgress, loading, error, refetch }
 */
export const useConceptProgress = (studentId, conceptCode, autoFetch = true) => {
  const [conceptProgress, setConceptProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConceptProgress = useCallback(async () => {
    if (!studentId || !conceptCode) {
      setError("Student ID and Concept Code are required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await AdaptiveLearningApi.getConceptProgress(studentId, conceptCode);
      setConceptProgress(data);
    } catch (err) {
      console.error("Error fetching concept progress:", err);
      setError(err.message || "Failed to fetch concept progress");
    } finally {
      setLoading(false);
    }
  }, [studentId, conceptCode]);

  useEffect(() => {
    if (autoFetch && studentId && conceptCode) {
      fetchConceptProgress();
    }
  }, [autoFetch, studentId, conceptCode, fetchConceptProgress]);

  return {
    conceptProgress,
    loading,
    error,
    refetch: fetchConceptProgress,
  };
};

/**
 * Hook to fetch all sessions for a student
 * @param {number} studentId - Student ID
 * @param {boolean} autoFetch - Auto fetch on mount (default: true)
 * @returns {Object} { sessions, loading, error, refetch }
 */
export const useLearningSessions = (studentId, autoFetch = true) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSessions = useCallback(async () => {
    if (!studentId) {
      setError("Student ID is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await AdaptiveLearningApi.getSessions(studentId);
      setSessions(data);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError(err.message || "Failed to fetch sessions");
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (autoFetch && studentId) {
      fetchSessions();
    }
  }, [autoFetch, studentId, fetchSessions]);

  return {
    sessions,
    loading,
    error,
    refetch: fetchSessions,
  };
};

/**
 * Hook to manage a learning session lifecycle
 * @param {number} studentId - Student ID
 * @returns {Object} Session management functions and state
 */
export const useSessionManager = (studentId) => {
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeSpentSeconds, setTimeSpentSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle"); // idle, starting, loading_questions, ready, error, submitting, completed

  /**
   * Start a new learning session (no automatic retry)
   */
  const startSession = useCallback(async (conceptCode, numberOfQuestions = 10) => {
    setLoading(true);
    setError(null);
    setStatus("starting");

    try {
      // Step 1: Create session
      const sessionData = await AdaptiveLearningApi.startSession(
        conceptCode,
        studentId,
        numberOfQuestions
      );
      setSession(sessionData);
      setStatus("loading_questions");

      // Step 2: Fetch questions (single attempt, no automatic retry)
      try {
        const questionsData = await AdaptiveLearningApi.getQuestions(sessionData.id);
        setQuestions(questionsData);
        setAnswers({});
        setCurrentQuestionIndex(0);
        setTimeSpentSeconds(0);
        setStatus("ready");
        setLoading(false);
        return sessionData;
      } catch (questionError) {
        // Questions not ready yet - show user-friendly error with retry button
        setError(
          questionError.message || 
          "Questions are still being generated. This usually takes 3-5 seconds. Please click 'Retry' to check again."
        );
        setStatus("error"); // Use "error" state instead of staying in "loading_questions"
        setLoading(false);
        throw questionError;
      }
    } catch (err) {
      console.error("Error starting session:", err);
      setError(err.message || "Failed to start session. Please try again.");
      setStatus("error"); // Use "error" state instead of going back to "idle"
      setLoading(false);
      throw err;
    }
  }, [studentId]);

  /**
   * Set answer for current question
   */
  const setAnswer = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  /**
   * Navigate to next question
   */
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  /**
   * Navigate to previous question
   */
  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  /**
   * Go to specific question
   */
  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  /**
   * Submit session answers
   */
  const submitSession = useCallback(async () => {
    if (!session) {
      throw new Error("No active session");
    }

    setLoading(true);
    setError(null);
    setStatus("submitting");

    try {
      const result = await AdaptiveLearningApi.submitAnswers(
        session.id,
        studentId,
        session.conceptCode,
        answers,
        timeSpentSeconds
      );

      setSession(result);
      setStatus("completed");

      return result;
    } catch (err) {
      console.error("Error submitting session:", err);
      setError(err.message || "Failed to submit session");
      setStatus("ready");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session, studentId, answers, timeSpentSeconds]);

  /**
   * Reset session state
   */
  const resetSession = () => {
    setSession(null);
    setQuestions([]);
    setAnswers({});
    setCurrentQuestionIndex(0);
    setTimeSpentSeconds(0);
    setError(null);
    setStatus("idle");
  };

  /**
   * Get current question
   */
  const currentQuestion = questions[currentQuestionIndex] || null;

  /**
   * Get answered question count
   */
  const answeredCount = Object.keys(answers).length;

  /**
   * Check if all questions are answered
   */
  const allAnswered = answeredCount === questions.length && questions.length > 0;

  return {
    session,
    questions,
    answers,
    currentQuestion,
    currentQuestionIndex,
    timeSpentSeconds,
    loading,
    error,
    status,
    answeredCount,
    allAnswered,
    startSession,
    setAnswer,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    submitSession,
    resetSession,
    setTimeSpentSeconds,
  };
};

export default {
  useAdaptiveLearningProgress,
  useConceptProgress,
  useLearningSessions,
  useSessionManager,
};

